import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Legend,
    YAxis,
    CartesianGrid,
    TooltipProps
} from 'recharts';
import { COLORS } from "../";
import { DataAttributes, DataStackedAttributes, BaseAxisChartProps } from '../types';
import { flattenStackedGraphData } from '../utils';
import { GraphTooltip } from '../shared/GraphTooltip';

interface BarGraphProps {
    data: DataAttributes[];
}
interface BarGraphStackedProps extends BaseAxisChartProps {
    data: DataStackedAttributes[];
    isStacked?: boolean;
    ticks?: string[];
    filterTick?: (tick: string | number) => boolean;
}

export const BarGraph: React.FC<BarGraphProps> = ({
    data,
}) => {
    return <BarGraphStacked
        data={data.map((item) => {
            return {
                x: item.x,
                y: [item.y],
                label: [item.label],
            };
        })}
        isStacked={false}
    />;
}

export const BarGraphStacked: React.FC<BarGraphStackedProps> = ({
    data,
    isStacked = false,
    ticks = [],
    filterTick,
}) => {

    // Constants for styling and layout
    const MARGIN = { top: 20, left: 20, right: 20, bottom: 0 };
    const TICK_FONT_SIZE = 10;
    const Y_AXIS_TICK_COUNT = 5;
    const Y_AXIS_WIDTH = 20;
    const TOOLTIP_OFFSET = 10;
    const BAR_RADIUS_ALL = [3, 3, 1, 1]; // top-left, top-right, bottom-right, bottom-left
    const BAR_RADIUS_TOP = [3, 3, 0, 0];
    const BAR_RADIUS_BOTTOM = [0, 0, 1, 1];

    const dataFlattened = flattenStackedGraphData(data);

    let maxIndex = -1;
    if (dataFlattened.length > 0 && dataFlattened[0]) {
        const yKeys = Object.keys(dataFlattened[0]).filter(key => key.startsWith('y'));
        yKeys.forEach(key => {
            const index = parseInt(key.replace('y', ''), 10);
            if (!isNaN(index) && index > maxIndex) {
                maxIndex = index;
            }
        });
    }

    const baseTicks = (ticks.length === 0)
        ? dataFlattened.map((item) => item.x)
        : ticks;
    const finalTicks = filterTick ? baseTicks.filter(filterTick) : baseTicks;

    const renderTooltip = (props: TooltipProps<number, string>) => {
        return <GraphTooltip {...props} />;
    };

    return (<ResponsiveContainer
        width={"100%"}
        height={"100%"}
    >
        <BarChart
            data={dataFlattened}
            margin={MARGIN}
        >
            <XAxis dataKey="x"
                tick={{ fontSize: TICK_FONT_SIZE, fontWeight: 'bold' }}
                interval={0}
                axisLine={false}
                tickLine={true}
                ticks={finalTicks}
            />
            <YAxis tick={{ fontSize: TICK_FONT_SIZE, fontWeight: 'bold' }}
                tickCount={Y_AXIS_TICK_COUNT}
                axisLine={false}
                tickLine={false}
                orientation="right"
                width={Y_AXIS_WIDTH}
            />
            <RechartsTooltip
                content={renderTooltip}
                position={{ x: 0, y: 0 }}
                offset={TOOLTIP_OFFSET}
                cursor={{ fill: 'rgba(206, 206, 206, 0.2)' }}
                isAnimationActive={false}
            />
            {dataFlattened && dataFlattened.length > 0 && dataFlattened[0] && Object.keys(dataFlattened[0]).map((key) => {
                if (key.startsWith('y')) {
                    const index = parseInt(key.replace('y', ''), 10);
                    if (isNaN(index)) return null;

                    const color = COLORS[index % COLORS.length];
                    const isTopBar = index === maxIndex;
                    const isBottomBar = index === 0;

                    const barProps: any = {
                        dataKey: key,
                        fill: color,
                        key: key,
                        stackId: isStacked ? 'stacked' : undefined,
                        isAnimationActive: false,
                    };

                    if (isTopBar && isBottomBar) {
                        barProps.radius = BAR_RADIUS_ALL;
                    } else if (isTopBar) {
                        barProps.radius = BAR_RADIUS_TOP;
                    } else if (isBottomBar) {
                        barProps.radius = BAR_RADIUS_BOTTOM;
                    }

                    return <Bar {...barProps} />;
                }
                return null;
            })}
        </BarChart>
    </ResponsiveContainer>
    );
};




